/**
 * Auth API typed bindings + React Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export type Role =
  | "superadmin"
  | "company_admin"
  | "company_operator"
  | "company_viewer";

export interface ImpersonationContext {
  company_id: string;
  company_slug: string;
  company_name: string;
}

export interface CurrentUser {
  id: string;
  username: string;
  role: Role;
  company_id: string | null;
  is_superadmin: boolean;
  has_totp: boolean;
  impersonating: ImpersonationContext | null;
}

export interface LoginRequest {
  username: string;
  password: string;
  totp_code?: string;
}

export interface LoginResponse {
  user_id: string;
  username: string;
  role: Role;
  company_id: string | null;
  requires_2fa: boolean;
  requires_2fa_setup: boolean;
}

export interface TotpFirstTimeSetupResponse {
  secret: string;
  otpauth_uri: string;
  issuer: string;
  account: string;
}

export interface InviteInfo {
  username: string;
  company_name: string | null;
  purpose: "activation" | "password_reset";
  expires_at: string;
}

export const ME_QUERY_KEY = ["auth", "me"] as const;

export function useCurrentUser() {
  return useQuery<CurrentUser | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>("/api/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Fetch /me and write it into the cache via setQueryData *before* the
 * mutation resolves. refetchQueries delivers the new data through the
 * store's notify callback, which leaves it visible only on the *next*
 * React render — and our pushState navigation lands before that, so the
 * router sees the stale /me and the user appears to need an F5. Manual
 * setQueryData makes the cache update synchronous.
 */
async function refreshMeIntoCache(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  try {
    const me = await api.get<CurrentUser>("/api/auth/me");
    qc.setQueryData(ME_QUERY_KEY, me);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      qc.setQueryData(ME_QUERY_KEY, null);
    } else {
      throw err;
    }
  }
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LoginRequest) => {
      const res = await api.post<LoginResponse>("/api/auth/login", payload);
      // Only the "fully signed in" path should pre-populate /me. If the
      // backend asked for 2FA-setup or a TOTP code, leave the cache as
      // anonymous — the caller will redirect to the appropriate page.
      if (!res.requires_2fa && !res.requires_2fa_setup) {
        await refreshMeIntoCache(queryClient);
      }
      return res;
    },
    onSuccess: (res) => {
      // Belt-and-suspenders: even if setQueryData inside mutationFn somehow
      // didn't propagate (e.g. a stale HMR'd observer), nudge the cache so
      // the next render is guaranteed to read fresh data instead of the
      // pre-login `null` value. Without this, users occasionally land on
      // the login page after a successful auth and need to F5.
      if (!res.requires_2fa && !res.requires_2fa_setup) {
        queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post<void>("/api/auth/logout");
      // Drop every cached company-scoped page so the next login starts
      // fresh, but DON'T qc.clear() — that nukes the active /me observer
      // and forces it back into isLoading=true (which is what's been
      // causing the "F5 to navigate" bug). setQueryData with null tells
      // useCurrentUser the user is anonymous, App routes to LoginPage.
      queryClient.removeQueries({
        predicate: (q) => q.queryKey[0] !== ME_QUERY_KEY[0],
      });
      queryClient.setQueryData(ME_QUERY_KEY, null);
    },
  });
}

export function useInviteInfo(token: string | null) {
  return useQuery<InviteInfo | null>({
    queryKey: ["invite", token],
    enabled: token !== null && token.length > 0,
    queryFn: () =>
      api.get<InviteInfo>(`/api/auth/invite/info?token=${encodeURIComponent(token!)}`),
    retry: false,
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: (payload: { token: string; new_password: string }) =>
      api.post<void>("/api/auth/invite/accept", payload),
  });
}

export function useTotpFirstTimeSetup() {
  return useMutation({
    mutationFn: () =>
      api.post<TotpFirstTimeSetupResponse>("/api/auth/2fa/setup-firsttime"),
  });
}

export function useTotpFirstTimeVerify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string }) => {
      const res = await api.post<LoginResponse>(
        "/api/auth/2fa/verify-firsttime",
        payload,
      );
      // Synchronous /me update so navigate("/") sees the authenticated user
      // on the very next render (no F5 needed).
      await refreshMeIntoCache(queryClient);
      return res;
    },
  });
}
